<script setup lang="ts">

import { onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth } from '@/shared/composables/useAuth';
import { useTheme } from '@/shared/composables/useTheme';
import { useLayoutScale } from '@/shared/composables/useLayoutScale';
import { useBeforeUnloadWarning } from '@/shared/composables/useBeforeUnloadWarning';
import { useGlobalDirtyRegistry } from '@/shared/composables/useGlobalDirtyRegistry';
import { useProjectCacheInvalidation } from '@/shared/composables/useProjectCacheInvalidation';
import { useProjectStore } from '@/shared/stores/project';
import MainLayout from '@/shared/components/layouts/MainLayout.vue';
import { ConfirmDialog } from 'primevue';
import Toast from 'primevue/toast';

const route = useRoute();
const auth = useAuth();
const projectStore = useProjectStore();
const { applyTheme } = useTheme();

useLayoutScale();
useProjectCacheInvalidation();

const { isDirty } = useGlobalDirtyRegistry();
useBeforeUnloadWarning(isDirty);

onMounted(async () => {
  // Initialize theme
  applyTheme();
  
  // Initialize authentication and load profile if user is already authenticated
  // Router guards will handle navigation, so no need to redirect here
  await auth.initialize();
  if (auth.isAuthenticated.value) {
    await projectStore.initialize();
  }
});

const showLayout = computed(() => {
  // Only show layout if authenticated and not on login page
  return auth.isAuthenticated.value && route.path !== '/login';
});
</script>

<template>
  <!-- Show loading indicator while auth is initializing -->
  <div v-if="auth.loading.value" class="loading-container">
    <div class="loading-spinner">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p>Loading...</p>
    </div>
  </div>
  
  <!-- Show main content once auth is resolved -->
  <template v-else>
    <MainLayout v-if="showLayout">
      <router-view />
    </MainLayout>
    <router-view v-else />
  </template>
  
  <Toast position="top-right"  />
  <ConfirmDialog :pt="{ 
    mask: { class: 'modal-backdrop-primevue' },
    message: { style: 'white-space: pre-line' }
  }"/>
</template>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background-color: var(--surface-ground);
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-spinner p {
  margin: 0;
  color: var(--text-color);
  font-size: 1rem;
}
</style>
